import styled from 'styled-components'

export const DetailHeader = styled.div`
  padding: 12px 16px;
  background: linear-gradient(135deg, #0294df08 0%, #0294df03 100%);
  border-bottom: 1px solid #f0f0f0;
  container-type: inline-size;
`

// Header components (matching ListingDetail pattern)
export const DetailHeaderInfo = styled.div`
  flex: 1;
  min-width: 0;
  margin-right: ${({ theme }) => theme.spacing.medium};
`

export const DetailHeaderBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex: 1;
  min-width: 0;
  gap: ${({ theme }) => theme.spacing.small};
`

export const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.small};
`

// Transaction info section (moved from header to body)
export const TransactionInfoSection = styled.div`
  padding: 16px 20px;
  background: linear-gradient(135deg, #0294df08 0%, #0294df03 100%);
  border-bottom: 1px solid #f0f0f0;
  container-type: inline-size;
`

export const TransactionInfoMainContainer = styled.div`
  display: flex;
  gap: 20px;
  align-items: flex-start;
  margin-bottom: 12px;
`

export const TransactionInfoContent = styled.div`
  flex: 1;
`

export const DetailTitle = styled.h1`
  font-size: 18px;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0 0 8px 0;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`

export const DetailSubtitle = styled.div`
  font-size: 13px;
  color: #6b6b6b;
  display: flex;
  align-items: center;
  gap: 4px;
  margin: 0;
  flex-wrap: wrap;
`

export const DetailHeaderMeta = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 3px;
`

// Quick Summary Chips container
export const QuickSummaryChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #f0f0f0;
`

// Clickable summary chip
export const SummaryChip = styled.button<{
  $variant?: 'success' | 'warning' | 'danger' | 'primary' | 'secondary' | 'info'
}>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 20px;
  border: none;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  background: ${({ $variant }) => {
    switch ($variant) {
      case 'success':
        return 'rgba(0, 179, 65, 0.1)'
      case 'warning':
        return 'rgba(255, 149, 0, 0.1)'
      case 'danger':
        return 'rgba(255, 59, 48, 0.1)'
      case 'primary':
        return 'rgba(2, 148, 223, 0.1)'
      case 'info':
        return 'rgba(88, 86, 214, 0.1)'
      default:
        return 'rgba(142, 142, 147, 0.1)'
    }
  }};

  color: ${({ $variant }) => {
    switch ($variant) {
      case 'success':
        return '#00B341'
      case 'warning':
        return '#FF9500'
      case 'danger':
        return '#FF3B30'
      case 'primary':
        return '#0294df'
      case 'info':
        return '#5856D6'
      default:
        return '#6b6b6b'
    }
  }};

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }

  svg {
    width: 14px;
    height: 14px;
  }
`
export const TransactionMapContainer = styled.div`
  height: 140px;
  min-height: 140px;
  width: 300px;
  min-width: 300px;
  border-radius: 6px;
  overflow: hidden;
  border: 1px solid #e5e5e5;
  position: relative;
  background: #f8f9fa;
  flex-shrink: 0;

  @container (max-width: 850px) {
    display: none;
  }

  .mapboxgl-popup-content {
    padding: 8px 10px;
    font-size: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  }

  .mapboxgl-ctrl-attrib {
    display: none;
  }
`

// Keep alias for backwards compatibility
export const PropertyTypeBadge = styled.span`
  display: inline-block;
  padding: 3px 6px;
  border-radius: 3px;
  font-size: 10px;
  font-weight: 500;
  background: rgba(142, 142, 147, 0.1);
  color: #8e8e93;
  margin-left: 6px;
`

export const DetailContent = styled.div`
  padding: 16px 20px;
`

export const WarningBox = styled.div`
  background: rgba(255, 149, 0, 0.1);
  border: 1px solid rgba(255, 149, 0, 0.2);
  border-radius: 5px;
  padding: 10px;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: #ff9500;
  margin-bottom: 12px;
`

export const DataGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`

export const DataGroup = styled.div`
  background: white;
  border: 1px solid #f0f0f0;
  border-radius: 6px;
  padding: 16px;
  margin-bottom: 12px;

  &:last-child {
    margin-bottom: 0;
  }
`

export const DataSectionTitle = styled.h3`
  font-size: 13px;
  font-weight: 600;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0 0 12px 0;
  display: flex;
  align-items: center;
  gap: 6px;

  svg {
    width: 14px;
    height: 14px;
    opacity: 0.7;
  }
`

export const DataRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #f8f8f8;

  &:last-child {
    border-bottom: none;
  }
`

export const DataLabel = styled.span`
  font-size: 13px;
  color: #6b6b6b;
`

export const DataValue = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: #1a1a1a;
  text-align: right;
`

export const PriceValue = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: #0294df;
  text-align: right;
`

export const DescriptionBox = styled.div`
  background: #f8f9fa;
  border-radius: 6px;
  padding: 12px;
  font-size: 12px;
  line-height: 1.5;
  color: #1a1a1a;
  white-space: pre-wrap;
  word-break: break-word;
  max-height: 300px;
  overflow-y: auto;
`

export const PropertyList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 12px;
`

export const PropertyItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px;
  background: #f8f9fa;
  border-radius: 5px;
`

export const PropertyNumber = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: #1a1a1a;
`

export const PropertyArea = styled.span`
  font-size: 10px;
  color: #6b6b6b;
`

export const LoadingState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 160px;
  color: #6b6b6b;
`

export const ErrorState = styled.div`
  text-align: center;
  padding: 48px 16px;
  color: #ff3b30;
`

// Participant styles
export const ParticipantsContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`

export const ParticipantGroup = styled.div``

export const ParticipantGroupHeader = styled.div<{ $role: 'buyer' | 'seller' }>`
  padding: 6px 12px;
  background: ${({ $role }) =>
    $role === 'buyer' ? 'rgba(2, 148, 223, 0.08)' : 'rgba(0, 179, 65, 0.08)'};
  border-radius: 6px 6px 0 0;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${({ $role }) => ($role === 'buyer' ? '#0294df' : '#00B341')};
  display: flex;
  align-items: center;
  justify-content: space-between;
`

export const ParticipantList = styled.div`
  border: 1px solid #f0f0f0;
  border-top: none;
  border-radius: 0 0 6px 6px;
`

export const ParticipantCard = styled.div`
  padding: 10px 12px;
  border-bottom: 1px solid #f8f8f8;

  &:last-child {
    border-bottom: none;
  }
`

export const ParticipantType = styled.div`
  font-size: 8px;
  color: #888;
  margin-bottom: 2px;
`

export const ParticipantName = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #1a1a1a;
  margin-bottom: 4px;
`

export const ParticipantDetails = styled.div`
  font-size: 11px;
  color: #6b6b6b;
  line-height: 1.4;

  > div {
    margin-bottom: 2px;

    &:last-child {
      margin-bottom: 0;
    }
  }
`

// Property styles
export const PropertyCard = styled.div`
  background: white;
  border: 1px solid #e5e5e5;
  border-radius: 6px;
  padding: 12px;
  margin-bottom: 12px;

  &:last-child {
    margin-bottom: 0;
  }
`

export const PropertyHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
  padding-bottom: 8px;
  border-bottom: 1px solid #f8f8f8;
`

export const MapMarkerBadge = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #2563eb;
  color: white;
  border: 3px solid white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: bold;
  flex-shrink: 0;
`

export const PropertyBadge = styled.span<{ $type?: string }>`
  display: inline-block;
  padding: 3px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  background: ${({ $type }) => {
    switch ($type) {
      case 'PPE':
        return 'rgba(2, 148, 223, 0.1)'
      case 'B-F':
        return 'rgba(0, 179, 65, 0.1)'
      case 'COP':
        return 'rgba(255, 149, 0, 0.1)'
      case 'DDP':
        return 'rgba(128, 90, 213, 0.1)'
      case 'DP':
        return 'rgba(255, 59, 48, 0.1)'
      default:
        return 'rgba(142, 142, 147, 0.1)'
    }
  }};
  color: ${({ $type }) => {
    switch ($type) {
      case 'PPE':
        return '#0294df'
      case 'B-F':
        return '#00B341'
      case 'COP':
        return '#FF9500'
      case 'DDP':
        return '#805AD5'
      case 'DP':
        return '#FF3B30'
      default:
        return '#8E8E93'
    }
  }};
`

export const PropertyId = styled.span`
  font-size: 10px;
  color: #888;
`

export const PropertyDetails = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 6px;
  margin-bottom: 6px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`

export const PropertyInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
`

export const PropertyInfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 12px;

  span:first-child {
    color: #888;
  }

  span:last-child {
    color: #1a1a1a;
    font-weight: 500;
  }
`

// Unit styles
export const PropertyUnits = styled.div`
  margin-top: 6px;
  padding-top: 6px;
  border-top: 1px solid #f8f8f8;
`

export const UnitsTitle = styled.div`
  font-size: 10px;
  font-weight: 600;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  margin-bottom: 5px;
`

export const UnitItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 5px 6px;
  background: #f8f9fa;
  border-radius: 3px;
  margin-bottom: 3px;

  &:last-child {
    margin-bottom: 0;
  }
`

export const UnitType = styled.span`
  font-size: 10px;
  font-weight: 500;
  color: #1a1a1a;
`

export const UnitDetails = styled.span`
  font-size: 10px;
  color: #6b6b6b;
  display: flex;
  gap: 6px;

  > span {
    display: flex;
    align-items: center;
    gap: 2px;
  }
`

// Map container
export const MapContainer = styled.div`
  height: 200px;
  min-height: 200px;
  border-radius: 5px;
  overflow: hidden;
  border: 1px solid #e5e5e5;
  margin-top: 10px;
  position: relative;
  background: #f8f9fa;

  .mapboxgl-popup-content {
    padding: 6px 8px;
    font-size: 10px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  }

  .mapboxgl-ctrl-attrib {
    display: none;
  }
`

// Collapsible section
export const CollapsibleSection = styled.div<{ $isOpen: boolean }>`
  border: 1px solid #f0f0f0;
  border-radius: 6px;
  overflow: hidden;
  margin-bottom: 12px;
`

export const CollapsibleHeader = styled.button`
  width: 100%;
  padding: 10px 12px;
  background: #fafafa;
  border: none;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
  color: #1a1a1a;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  &:hover {
    background: #f5f5f5;
  }

  svg {
    width: 14px;
    height: 14px;
    color: #888;
    transition: transform 0.2s;
  }
`

export const CollapsibleContent = styled.div<{ $isOpen: boolean }>`
  display: ${(props) => (props.$isOpen ? 'block' : 'none')};
  padding: 12px;
  background: white;
`

// Linked land section — rendered inside the PropertyCard
export const LinkedLandSection = styled.div`
  margin: 6px -12px -12px -12px;
  padding: 0 12px 12px 12px;
  background: #fafbff;
  border-top: 1px solid #e0e7ff;
  border-radius: 0 0 6px 6px;
`

// Linked land divider (dashed line with centered label)
export const LinkedLandDivider = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 12px 0;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    top: 50%;
    height: 1px;
    background: repeating-linear-gradient(
      to right,
      #e5e5e5 0,
      #e5e5e5 4px,
      transparent 4px,
      transparent 7px
    );
  }
`

export const LinkedLandLabel = styled.span`
  position: relative;
  padding: 2px 12px;
  font-size: 9px;
  font-weight: 500;
  letter-spacing: 0.5px;
  color: #2563eb;
  background: #f0f7ff;
  border-radius: 8px;
  text-transform: uppercase;
`

// Back button for land detail sub-view
export const LandDetailBackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  border: none;
  background: none;
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.primary};
  cursor: pointer;
  border-radius: 4px;

  &:hover {
    background: rgba(2, 148, 223, 0.08);
  }

  svg {
    width: 14px;
    height: 14px;
  }
`
